import {GridColumns, GridRenderCellParams} from '@mui/x-data-grid';
import {FormattedMessage} from 'react-intl';
import {ColorField} from 'src/components/common/ColorField';
import {TableIdCell} from 'src/components/common/TableIdCell';
import {UserTypeEnum, UsersSortFieldEnum} from 'src/enums';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {DeleteUser} from '../DeleteUser';
import {UserCell} from '../UserCell';
import dayjs from 'dayjs';
import {ContractDTO} from 'src/types';
import {Box} from '@mui/material';
import {EmployeeCell} from '../EmployeeCell';
import {ResendRegistration} from '../ResendRegistration';
import {RegistrationStatusEnum} from 'src/enums/users';

/**
 * Takes what the caller may see rather than what they are called. The role name it replaced
 * could not answer for a business-defined role, and the column would simply have stayed hidden.
 */
export const columnConfigUsers = (
    canDeleteUser = true,
    scopedToOwnSlice = false
): GridColumns => [
    {
        field: 'name',
        renderHeader: (_params) => <FormattedMessage id={'nameColumnHeader'}/>,
        flex: 2,
        minWidth: 200,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => {
            if (params.row.userType === UserTypeEnum.EMPLOYEE && params.row?.employee) {
                return <EmployeeCell data={params.row}/>;
            }

            return <UserCell data={params.row}/>;
        },
    },
    {
        field: UsersSortFieldEnum.ROLE,
        renderHeader: (_params) => <FormattedMessage id={'userRoleColumnHeader'}/>,
        flex: 1,
        minWidth: 135,
        cellClassName: 'ampTableCell',
        renderCell: (params) =>
            params.row.userType ? <ColorField label={params.row.userType}/> : '',
    },

    {
        field: UsersSortFieldEnum.CONTRACT_STATUS,
        renderHeader: (_params) => <FormattedMessage id={'contractColumnHeader'}/>,
        flex: 1.2,
        minWidth: 100,
        sortable: true,
        cellClassName: 'ampTableCell',
        renderCell: (params) => {
            const contracts = params.row.contracts;
            return (
                contracts && (
                    <Box sx={{display: 'flex', justifyContent: 'end', gap: 2}}>
                        {[...contracts]
                            .sort((a, b) => {
                                // Priority order based on backend statuses: active contracts first
                                const priorityOrder = {
                                    'OPERATION': 1,          // Active - highest priority
                                    'INSTALLATION': 2,       // Active setup
                                    'WAIT_FOR_SIGNATURE': 3, // Pending
                                    'ARCHIVED': 4            // Inactive - lowest priority
                                };

                                const aPriority = priorityOrder[a.status as keyof typeof priorityOrder] || 999;
                                const bPriority = priorityOrder[b.status as keyof typeof priorityOrder] || 999;

                                // If same priority, sort by creation date (newest first)
                                if (aPriority === bPriority) {
                                    return b.created - a.created;
                                }

                                return aPriority - bPriority;
                            })
                            .slice(0, 1)
                            ?.map((contract: ContractDTO) => (
                                <ColorField
                                    key={contract.id}
                                    label={contract.status}
                                />
                            ))}
                    </Box>
                )
            );
        },
    },
    {
        field: UsersSortFieldEnum.EMAIL,
        renderHeader: (_params) => <FormattedMessage id={'emailColumnHeader'}/>,
        sortable: false,
        flex: 2.5,
        minWidth: 240,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => params.row.emailAddress,
    },
    {
        field: UsersSortFieldEnum.CREATED,
        renderHeader: (_params) => <FormattedMessage id={'dateColumnHeader'}/>,
        flex: 1,
        minWidth: 100,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => dayjs(params.row.created).format('DD/MM/YY'),
    },
    {
        field: 'registrationStatus',
        renderHeader: (_params) => <FormattedMessage id={'statusColumnHeader'}/>,
        flex: 1.0,
        minWidth: 130,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => (
            <ColorField label={params.row.registrationStatus || 'N/A'}/>
        ),
    },
    {
        field: UsersSortFieldEnum.LOCATION,
        renderHeader: (_params) => <FormattedMessage id={'locationColumnHeader'}/>,
        flex: 4,
        minWidth: 300,
        sortable: true,
        cellClassName: 'ampTableCell',
        renderCell: (params) => {
            if (params.row.location) {
                return scopedToOwnSlice ? (
                    <TableIdCell
                        businessId={params.row.business.id}
                        label={params.row.location.name}
                        link={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations, params.row.location.id])}
                        withoutCopy
                    />
                ) : (
                    params.row.location.name
                );
            }
            return '';
        },
    },
    ...(canDeleteUser
        ? [
            {
                field: 'actions',
                width: 120,
                headerName: '',
                sortable: false,
                cellClassName: 'ampTableCell',
                align: 'right' as const,
                headerAlign: 'right' as const,
                renderCell: (params: GridRenderCellParams) => (
                    params.row.business && (
                        <Box sx={{display: 'flex', gap: 1}}>
                            {params.row.registrationStatus === RegistrationStatusEnum.PENDING_HASH && (
                                <ResendRegistration row={params.row}/>
                            )}
                            <DeleteUser row={params.row}/>
                        </Box>
                    )
                ),
            },
        ]
        : []),
];
